import { useState, useEffect, useCallback, useMemo } from 'react';

// Mirrors server/utils/surveyBranching.js's isQuestionVisible — kept as a
// small standalone copy (client and server are separate bundles) so the
// player can show/hide questions live as the student answers, without a
// round trip. The server re-evaluates the same condition on submit, so a
// client-side bug here can't leak a hidden question's answer into storage.
function isQuestionVisible(question, answers) {
  const showIf = question.settings && question.settings.showIf;
  if (!showIf) return true;
  const target = answers[showIf.questionId];
  if (!target || !target.optionIds || target.optionIds.size === 0) return false;
  return showIf.optionIds.some(id => target.optionIds.has(id));
}

/**
 * The answer state + branching visibility + submit-payload building shared
 * by the authenticated player (useSurveyPlayer) and the public magic-link
 * player (usePublicSurveyPlayer) — everything except how `data` is loaded
 * and where the built payload gets submitted.
 *
 * @param {object|null} data - the getAssignmentDetails-shaped payload
 *   (title/description/questions/responses/completed_at), or null while loading
 */
export function useSurveyResponseState(data) {
  const [answers, setAnswers] = useState({}); // question_id -> { optionIds: Set, answerText: string }
  // One question shown at a time (card + Previous/Next, like the quiz player).
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  useEffect(() => {
    if (!data) return;
    const initialAnswers = {};
    for (const r of data.responses || []) {
      initialAnswers[r.question_id] = {
        optionIds: new Set(r.option_ids || []),
        answerText: r.answer_text || '',
      };
    }
    setAnswers(initialAnswers);
    setCurrentQuestionIndex(0);
  }, [data]);

  const isReadOnly = Boolean(data?.completed_at);

  const visibleQuestions = useMemo(() => {
    if (!data) return [];
    return data.questions.filter(q => isQuestionVisible(q, answers));
  }, [data, answers]);

  const hasClosingStep = Boolean(data?.closing_message) && !isReadOnly;
  const finalStepIndex = Math.max(visibleQuestions.length - 1, 0) + (hasClosingStep ? 1 : 0);

  // Answering an earlier branching question can make a later one disappear
  // (or reappear) between renders — keep the index in range of whatever is
  // visible right now instead of landing on an undefined question.
  useEffect(() => {
    if (currentQuestionIndex > finalStepIndex) {
      setCurrentQuestionIndex(finalStepIndex);
    }
  }, [currentQuestionIndex, finalStepIndex]);

  const goToNext = useCallback(() => {
    setCurrentQuestionIndex(i => Math.min(i + 1, finalStepIndex));
  }, [finalStepIndex]);

  const goToPrevious = useCallback(() => {
    setCurrentQuestionIndex(i => Math.max(i - 1, 0));
  }, []);

  const getAnswer = useCallback((questionId) => answers[questionId] || { optionIds: new Set(), answerText: '' }, [answers]);

  const setSingleChoice = (questionId, optionId) => setAnswers(prev => ({
    ...prev, [questionId]: { optionIds: new Set([optionId]), answerText: prev[questionId]?.answerText || '' },
  }));

  const toggleMultiChoice = (questionId, optionId, maxSelections) => setAnswers(prev => {
    const current = new Set(prev[questionId]?.optionIds || []);
    if (current.has(optionId)) {
      current.delete(optionId);
    } else {
      if (maxSelections && current.size >= maxSelections) return prev;
      current.add(optionId);
    }
    return { ...prev, [questionId]: { optionIds: current, answerText: prev[questionId]?.answerText || '' } };
  });

  const setAnswerText = (questionId, text) => setAnswers(prev => ({
    ...prev, [questionId]: { optionIds: prev[questionId]?.optionIds || new Set(), answerText: text },
  }));

  const getMissingRequired = () => visibleQuestions.filter(q => {
    if (!q.required) return false;
    const a = getAnswer(q.id);
    return a.optionIds.size === 0 && !a.answerText.trim();
  });

  const buildResponsesPayload = () => visibleQuestions.map(q => {
    const a = getAnswer(q.id);
    return {
      question_id: q.id,
      option_ids: [...a.optionIds],
      answer_text: a.answerText || null,
    };
  });

  return {
    buildResponsesPayload, currentQuestionIndex, getAnswer, getMissingRequired, goToNext, goToPrevious,
    hasClosingStep, isReadOnly, setAnswerText, setCurrentQuestionIndex, setSingleChoice, toggleMultiChoice, visibleQuestions,
  };
}
